import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorFallbackProps {
  readonly error: Error;
  readonly reset: () => void;
}

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback: (props: ErrorFallbackProps) => ReactNode;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
  readonly resetKeys?: readonly unknown[];
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

function changed(
  previous: readonly unknown[],
  next: readonly unknown[],
): boolean {
  return (
    previous.length !== next.length ||
    previous.some((value, index) => !Object.is(value, next[index]))
  );
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (
      this.state.error !== null &&
      changed(previous.resetKeys ?? [], this.props.resetKeys ?? [])
    ) {
      this.setState({ error: null });
    }
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return this.props.fallback({
        error: this.state.error,
        reset: this.reset,
      });
    }
    return this.props.children;
  }
}
